// src/models/reportModel.js
import api from '../services/api';




// Get revenue summary
export const getRevenueSummary = async (startDate, endDate) => {
    try {
        // Manually construct query string to ensure params are sent correctly
        const url = `/reports/revenue?startDate=${startDate}&endDate=${endDate}`;
        console.log('Requesting revenue summary:', url);
        const response = await api.get(url);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching revenue summary' };
    }
};

// Get monthly revenue
export const getMonthlyRevenue = async (year = new Date().getFullYear()) => {
    try {
        const response = await api.get(`/reports/monthly-revenue/${year}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching monthly revenue' };
    }
};

// Get daily revenue
export const getDailyRevenue = async (year, month) => {
    try {
        const response = await api.get(`/reports/daily-revenue/${year}/${month}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching daily revenue' };
    }
};

// Get building revenue
export const getBuildingRevenue = async (startDate, endDate, buildingId = null) => {
    try {
        const response = await api.get('/reports/building-revenue', { params: { startDate, endDate, buildingId } });
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching building revenue' };
    }
};

// Get owner revenue
export const getOwnerRevenue = async (startDate, endDate, ownerId = null) => {
    try {
        const response = await api.get('/reports/owner-revenue', { params: { startDate, endDate, ownerId } });
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching owner revenue' };
    }
};

// Get payment logs
export const getPaymentLogs = async (filters = {}) => {
    try {
        const response = await api.get('/reports/payment-logs', { params: filters });
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching payment logs' };
    }
};

// Get occupancy statistics
export const getOccupancyStats = async (date = null) => {
    try {
        const response = await api.get('/reports/occupancy', { params: { date } });
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching occupancy statistics' };
    }
};

// Get tenant payment status
export const getTenantPaymentStatus = async () => {
    try {
        const response = await api.get('/reports/tenant-payments');
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching tenant payment status' };
    }
};

// Get annual summary
export const getAnnualSummary = async (year = new Date().getFullYear()) => {
    try {
        const response = await api.get(`/reports/annual-summary/${year}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching annual summary' };
    }
};

// Print payment receipt
export const printPaymentReceipt = async (paymentId) => {
    try {
        const response = await api.get(`/reports/print-receipt/${paymentId}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error printing receipt' };
    }
};

// Report templates
export const getReportTemplates = async (reportType = null) => {
    try {
        const response = await api.get('/reports/templates', { params: { reportType } });
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching report templates' };
    }
};

export const saveReportTemplate = async (templateData) => {
    try {
        const response = await api.post('/reports/templates', templateData);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error saving report template' };
    }
};

export const updateReportTemplate = async (id, templateData) => {
    try {
        const response = await api.put(`/reports/templates/${id}`, templateData);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error updating report template' };
    }
};

export const deleteReportTemplate = async (id) => {
    try {
        const response = await api.delete(`/reports/templates/${id}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error deleting report template' };
    }
};

// Update monthly summaries (admin only)
export const updateMonthlySummaries = async (year, month) => {
    try {
        const response = await api.post('/reports/update-summaries', { year, month });
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error updating monthly summaries' };
    }
};

// Helper functions
export const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
    }).format(amount || 0);
};

export const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    }).format(date);
};

export const formatMonth = (month) => {
    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[month - 1];
};

export const getLastNMonths = (n = 12) => {
    const months = [];
    const today = new Date();

    for (let i = 0; i < n; i++) {
        const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
        months.push({
            month: date.getMonth() + 1,
            year: date.getFullYear(),
            label: `${formatMonth(date.getMonth() + 1)} ${date.getFullYear()}`
        });
    }

    return months;
};

export const getMonthStartEnd = (year, month) => {
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0);

    const formatLocal = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    return {
        startDate: formatLocal(startDate),
        endDate: formatLocal(endDate)
    };
};

export const getCurrentMonthStartEnd = () => {
    const today = new Date();
    return getMonthStartEnd(today.getFullYear(), today.getMonth() + 1);
};

export const getCurrentYearStartEnd = () => {
    const year = new Date().getFullYear();
    return {
        startDate: `${year}-01-01`,
        endDate: `${year}-12-31`
    };
};

export const getQuarterStartEnd = (year, quarter) => {
    const startMonth = (quarter - 1) * 3 + 1;
    const endMonth = quarter * 3;

    const startDate = new Date(year, startMonth - 1, 1);
    const endDate = new Date(year, endMonth, 0);

    const formatLocal = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    return {
        startDate: formatLocal(startDate),
        endDate: formatLocal(endDate)
    };
};

export const getCurrentQuarterStartEnd = () => {
    const today = new Date();
    const quarter = Math.floor(today.getMonth() / 3) + 1;
    return getQuarterStartEnd(today.getFullYear(), quarter);
};

// Report types
export const REPORT_TYPES = [
    { value: 'revenue', label: 'Revenue Report' },
    { value: 'building', label: 'Building Report' },
    { value: 'owner', label: 'Owner Report' },
    { value: 'occupancy', label: 'Occupancy Report' },
    { value: 'tenant', label: 'Tenant Payment Report' }
];

// Time period options
export const TIME_PERIOD_OPTIONS = [
    { value: 'current_month', label: 'Current Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'current_quarter', label: 'Current Quarter' },
    { value: 'last_quarter', label: 'Last Quarter' },
    { value: 'current_year', label: 'Current Year' },
    { value: 'last_year', label: 'Last Year' },
    { value: 'custom', label: 'Custom Range' }
];

export default {
    getRevenueSummary,
    getMonthlyRevenue,
    getDailyRevenue,
    getBuildingRevenue,
    getOwnerRevenue,
    getPaymentLogs,
    getOccupancyStats,
    getTenantPaymentStatus,
    getAnnualSummary,
    printPaymentReceipt,
    getReportTemplates,
    saveReportTemplate,
    updateReportTemplate,
    deleteReportTemplate,
    updateMonthlySummaries,
    formatCurrency,
    formatDate,
    formatMonth,
    getLastNMonths,
    getMonthStartEnd,
    getCurrentMonthStartEnd,
    getCurrentYearStartEnd,
    getQuarterStartEnd,
    getCurrentQuarterStartEnd,
    REPORT_TYPES,
    TIME_PERIOD_OPTIONS
};